import { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import { Form, Typography, Divider, Button, Tooltip, Collapse, message, Modal } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  GiftOutlined,
  PercentageOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { LoyaltyBadge, type LoyaltyRedeemResult } from './LoyaltyBadge';
import type { FormInstance } from 'antd';
import { OrderTypeToggle } from '../OrderTypeToggle';
import { StoreBranchField } from './StoreBranchField';
import { LiveSessionPicker } from '../LiveSessionPicker';
import { CustomerSearchField } from './CustomerSearchField';
import { CustomerDialog } from './CustomerDialog';
import {
  buildAddressBody,
  buildDeliveryNote,
  entryRecipients,
  isDeliveryEdited,
  matchRecipient,
  normalizePhone,
  readAddressLocationId,
  readAddressLocationName,
  takeDeliverySnapshot,
  type AddressBookEntry,
  type AddressLocationRef,
  type DeliveryAddressSource,
} from './addressBook';
import { SelectedCustomerCard } from './SelectedCustomerCard';
import { VoucherModal, type VoucherItem } from './VoucherModal';
import type { PromotionFreeItem } from '../PromotionGiftBanner';
import { buildEvaluateItems } from '../../utils/buildEvaluateItems';
import { resolveAppliedPromotionIds } from '../../utils/resolveAppliedPromotionIds';
import { isVoucherGiftItem } from '../../utils/giftItems';
import { useRbac } from '@/hooks/useRbac';
import { customerEditAbilities, pickEditableCustomerFields } from '@/utils/customerFieldPermissions';
import { useB2BCreditOverLimitCustomers } from '@/hooks/useB2BCreditOverLimitCustomers';
import { TradeDiscountModal } from './TradeDiscountModal';
import {
  CANCEL_SNAPSHOT_FIELDS,
  CUSTOMER_DIALOG_FIELDS,
  isCustomerDialogComplete,
  deliveryDaysRules,
  FIELDS_RESET_ON_USER_TYPE_CHANGE,
} from './customerFormSchema';
import { CustomerCoreFields } from './CustomerCoreFields';
import { DeliveryDaysSelect } from './DeliveryDaysSelect';
import { DeliveryExtraFields } from './DeliveryExtraFields';
import { InvoiceSection } from './InvoiceSection';
import { UserTypeToggle } from './UserTypeToggle';
import type {
  OrderType,
  CommissionStaff,
  FulfillmentMode,
  UserType,
  PosCustomerSearchItem,
  OrderItem,
} from '../../types';
import { sumVoucherDiscount, hasFreeshipVoucher } from '../../utils/voucherTotals';
import { computePosTotals } from '../../utils/orderItemCalc';
import { buildCustomerProfileUpdate } from '../../utils/buildCustomerProfileUpdate';
import { customerService } from '@/services/customer.service';
import { customerKeys } from '@/services/queryKeys';
import { fetchCustomerAddresses } from '@/hooks/useCustomers';
import { useDebounce } from '@/hooks/useDebounce';
import { usePosEvaluateCart } from '@/hooks/usePromotions';
import { formatVND } from '@/utils/format';
import { sumNonFlashPromotionDiscount } from '@/utils/promotionDiscount';
import {
  customerValidationRequirements,
  STRICT_CUSTOMER_VALIDATION_POLICY,
  type CustomerValidationPolicy,
} from '../../utils/customerCheckout';
import './CustomerSidebar.css';

const { Text } = Typography;

export interface CustomerSidebarHandle {
  openCustomerDialog(mode?: 'add' | 'edit'): void;
}

interface Props {
  form: FormInstance;
  orderType: OrderType;
  onOrderTypeChange: (v: OrderType) => void;
  selectedStoreId?: string;
  selectedSessionId?: string;
  onSessionChange: (sessionId: string, commissionStaff: CommissionStaff[]) => void;
  hasItems: boolean;
  onClearItems: () => void;
  fulfillmentMode?: FulfillmentMode;
  cartItems: OrderItem[];
  variant?: 'compact' | 'inline';
  /**
   * Bật cảnh báo đỏ "chưa chọn khách hàng". Chỉ bật SAU KHI user bấm "Ghi hoá đơn
   * và thanh toán" — giỏ mới mở chưa có khách là bình thường, hiện đỏ ngay từ đầu
   * là nhiễu.
   */
  showNoCustomerAlert?: boolean;
  /** Nút phụ trong thẻ khách (variant compact) — vd nút hiện CCCD gốc ở trang sửa đơn. */
  customerCardExtra?: React.ReactNode;
  cccdMasked?: boolean;
  onRevealCccd?: () => void;
  /**
   * Báo lên POSPage khi CustomerDialog mở/đóng. POS dùng để tắt hotkey đổi tab
   * (F1/F2/Alt+N) lúc thu ngân đang nhập thông tin khách.
   *
   * CustomerDialog mount thường trực + `destroyOnClose={false}` nên node
   * `.ant-modal-wrap` ở lại DOM sau khi đóng — không thể dò DOM để biết nó có
   * đang mở hay không, bắt buộc phải đẩy state React ra ngoài.
   */
  onDialogOpenChange?: (open: boolean) => void;
  /**
   * Ẩn thanh "THÔNG TIN ĐƠN HÀNG" trên đỉnh khối. POS bật vì tiêu đề đó nằm xa
   * cụm tiền nó gọi tên — `OrderInfoPanel` ở dưới đã có tiêu đề riêng kèm số
   * sản phẩm. Mặc định `false` để Orders/edit giữ nguyên giao diện cũ.
   */
  hideHeader?: boolean;
  /** Explicit validation context. Omitted by Orders/edit and legacy callers. */
  validationPolicy?: CustomerValidationPolicy;
  onRetryValidationPolicy?: () => void;
  /**
   * TCR-1069 review round 3 (finding A): bật gọi `/pos/promotions/evaluate` +
   * hiện chip "Tự động áp dụng". CHỈ `POS/index.tsx` truyền `true` — sidebar
   * này còn được `Orders/edit.tsx` dựng lại với `cartItems={orderItems}` khác
   * rỗng cho MỌI đơn đã lưu, nhưng `OrderInfoPanel` ở màn đó không nhận
   * `promotionDiscount` và `payable` lấy từ `order.summary.total` đã chốt —
   * bật vô điều kiện thì chip hiện số giảm không khớp gì với tổng tiền đang
   * thấy, cộng một API call lãng phí mỗi lần mở sửa đơn. Mặc định `false` để
   * Orders/edit giữ nguyên hành vi cũ mà không cần sửa gì ở đó.
   */
  showAutoPromotions?: boolean;
  /**
   * TCR-1069: hình thức thanh toán đang chọn, gửi kèm `/pos/promotions/evaluate`.
   *
   * Bắt buộc từ lúc modal bắt đầu nói lý do "chưa đủ điều kiện": CTKM có
   * `conditions.payment_methods` (vd FREESHIPT8) mà không nhận được field này
   * sẽ luôn trượt, và modal sẽ nói "Chỉ áp dụng cho hình thức thanh toán:
   * cash, ..." ngay khi thu ngân ĐANG chọn tiền mặt — đúng kiểu nói sai mà
   * ticket này đang đi sửa.
   */
  paymentMethod?: string;
  /** ID đơn đang sửa, dùng để hoàn nhập khoản CK đang hold vào hạn mức modal. */
  rebateOrderId?: string;
  /** TCR-2055: trần CK tích luỹ = tiền hàng còn lại sau voucher + điểm (từ pipeline POS). */
  tradeCap?: number;
  /** TCR-2055: số khách phải trả thật khi CK tích luỹ = a (từ pipeline POS). */
  previewTradePayable?: (amount: number) => number;
  /**
   * TCR-1074: báo lên POSPage khi VoucherModal resolve xong gift item(s) cho
   * voucher vừa áp/gỡ — POSPage tự thay thế TOÀN BỘ dòng quà VOUCHER trong giỏ
   * bằng danh sách mới. Optional: Orders/edit.tsx không truyền, giữ nguyên
   * hành vi cũ ở màn đó (áp voucher không tự thêm quà — ngoài phạm vi TCR-1074).
   *
   * **HỢP ĐỒNG BẮT BUỘC** (fix round 1, C1/M1): `giftItems` truyền vào đây
   * PHẢI CHỈ chứa quà nguồn VOUCHER (`isVoucherGiftItem(i) === true`) — KHÔNG
   * được lẫn dòng hàng thường, và KHÔNG được lẫn quà CHƯƠNG TRÌNH KHUYẾN MÃI
   * (`gift_source: 'promotion'`, thêm qua `PromotionGiftBanner`). Caller
   * (`POS/index.tsx`/`Orders/edit.tsx`) thay thế bằng cách
   * `[...prev.filter((i) => !isVoucherGiftItem(i)), ...giftItems]` — CHỦ ĐỘNG
   * giữ nguyên quà CTKM trong `prev`. Nếu `giftItems` lỡ lẫn quà CTKM (hoặc
   * dòng hàng thường) vào, phần tử đó xuất hiện HAI LẦN trong kết quả (một lần
   * từ `prev` được giữ, một lần từ `giftItems`) — hai dòng cùng `id` khiến
   * `computePosMoney`/`groupAndAllocateVat` ném lỗi key trùng và màn hình
   * trắng khi tính tiền. Mọi nơi trong file này gọi `onGiftItemsChange` PHẢI
   * lọc bằng `isVoucherGiftItem`, không phải `is_gift` thô.
   */
  onGiftItemsChange?: (giftItems: OrderItem[]) => void;
  /**
   * Quà tặng từ CTKM cho giỏ hiện tại (`free_items` của kết quả evaluate).
   * `CustomerSidebar` là nơi DUY NHẤT gọi evaluate, nên nó phải báo ra ngoài —
   * `POSPage`/`OrderEditPage` mới là nơi sở hữu giỏ hàng và có quyền thêm
   * dòng. Chỉ khớp `free_items` THẬT khi `showAutoPromotions` bật VÀ kết quả
   * evaluate còn tươi (`promotionEvaluationFresh`) — cùng điều kiện đang gate
   * `appliedPromotions`, để không báo quà của một payload đã cũ.
   */
  onFreeItemsChange?: (freeItems: PromotionFreeItem[]) => void;
  /**
   * TCR-1403 — tập `promotion_id` còn ĐANG hợp lệ theo evaluate mới nhất,
   * dùng để `POSPage`/`OrderEditPage` (qua `usePromotionGiftAdd`) tự gỡ quà
   * CTKM (`gift_source: 'promotion'`) đã có sẵn trong giỏ nhưng không còn
   * nằm trong tập này (vd sản phẩm điều kiện vừa bị xoá). CHỈ gọi khi có tín
   * hiệu CHẮC CHẮN (`resolveAppliedPromotionIds` khác `null`) — evaluate mới
   * vừa về, hoặc giỏ không còn dòng hàng THẬT nào để chấm điều kiện nên chắc
   * chắn không CTKM nào còn thoả. KHÔNG gọi khi đang đánh giá lại/stale, để
   * tránh gỡ nhầm quà còn hợp lệ trong lúc chưa biết kết quả.
   */
  onAppliedPromotionIdsChange?: (ids: string[]) => void;
  /**
   * TCR-1146: bấm "Xác nhận" trong dialog sửa nhanh thì ghi luôn thông tin vừa
   * sửa vào hồ sơ khách (`PUT /customers/:id`), không đợi tới lúc thanh toán.
   *
   * Gắn với setting `pos_auto_sync_customer_enabled` (TCR-1071) — cùng một
   * chính sách "đơn POS được phép cập nhật hồ sơ khách", nên dialog không được
   * tự ý ghi khi tenant đã tắt. `Orders/edit.tsx` không truyền ⇒ `false` ⇒ giữ
   * nguyên hành vi cũ ở màn sửa đơn.
   */
  persistCustomerOnSave?: boolean;
  /**
   * TCR-1353 — khoản điểm ĐÃ CHỐT trên đơn (màn sửa đơn đọc từ
   * `order.loyalty_redeem_points`/`order.loyalty_redeem_value` đã lưu, KHÔNG
   * phải giá trị đang gõ dở trên form). Có giá trị này thì `LoyaltyBadge`
   * được mời hiện dòng "Đã dùng X điểm = Yđ" + số dư/hạng bất kể hàng rào
   * `loyaltyEnabled` (TCR-1253) đang tắt hay bật — hàng rào đó chỉ chặn việc
   * ĐỔI điểm mới, không phải việc hiển thị khoản đã đổi. `POS/index.tsx`
   * không truyền ⇒ `undefined` ⇒ giữ nguyên hành vi cũ (badge ẩn khi
   * `loyaltyEnabled=false`).
   */
  savedRedemption?: { points: number; value: number };
  /**
   * TCR-1353 — cho phép sửa khoản điểm đã chốt ở trên hay không (chỉ có ý
   * nghĩa khi có `savedRedemption`). Mặc định `true` để giữ nguyên hành vi
   * mời đổi điểm như trước nếu caller không truyền. Màn sửa đơn sẽ truyền
   * `false` khi đơn không còn ở trạng thái được sửa.
   */
  loyaltyEditable?: boolean;
  /**
   * FINAL-I1 (final review) — id khách hàng GỐC của đơn đang sửa
   * (`order.user_id`, `Orders/edit.tsx` truyền). CHỈ dùng để khoá nguồn dữ
   * liệu của `LoyaltyBadge` (số dư/hạng/điểm đang giữ) — KHÔNG thay thế
   * `customerUserId` (field form, vẫn là nguồn ĐÚNG cho voucher/khuyến mãi/
   * chiết khấu tích luỹ B2B, vì các luồng đó re-evaluate lại theo đúng khách
   * ĐANG chọn trên form, kể cả khi vừa đổi khách).
   *
   * Lý do cần tách riêng: payload PATCH của màn sửa đơn KHÔNG gửi
   * `customer_id` (chỉ `orderService.updateCustomer` mới gửi field đó).
   * Nếu `LoyaltyBadge` vẫn đọc `customerId` từ khách ĐANG chọn trên form
   * (`customerUserId`) — ví dụ nhân viên bấm ✕ đổi sang khách B rồi bấm
   * "Dùng điểm" — badge sẽ hiện số dư/hạng của B và ghi điểm tính theo B vào
   * form, nhưng lúc Lưu, backend không biết khách đã đổi (thiếu
   * `customer_id`) nên coi `loyalty_redeem_points` là sửa cho khách A
   * (`order.user_id`) — TRỪ NHẦM điểm của A theo một con số tính từ số dư
   * của B. Ruling 8 (Task 6) cũng cấm hẳn việc đổi điểm cho khách MỚI trong
   * cùng một request đổi khách — khoá badge về `order.user_id` là đúng ràng
   * buộc đó ngay từ khâu hiển thị, không đợi tới lúc BE từ chối.
   *
   * `POS/index.tsx` không truyền ⇒ `undefined` ⇒ `orderCustomerId ??
   * customerUserId` rơi về `customerUserId` y hệt trước đây — KHÔNG đổi hành
   * vi màn tạo đơn.
   */
  orderCustomerId?: string;
}

const INLINE_LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: '#555',
  fontWeight: 600,
  flexShrink: 0,
  width: 110,
};

export const CustomerSidebar = forwardRef<CustomerSidebarHandle, Props>(function CustomerSidebar(
  {
    form,
    orderType,
    onOrderTypeChange,
    selectedStoreId,
    selectedSessionId,
    onSessionChange,
    cartItems,
    fulfillmentMode = 'INSTANT',
    paymentMethod,
    rebateOrderId,
    tradeCap,
    previewTradePayable,
    variant = 'compact',
    hideHeader = false,
    showNoCustomerAlert = false,
    customerCardExtra,
    cccdMasked,
    onRevealCccd,
    onDialogOpenChange,
    validationPolicy = STRICT_CUSTOMER_VALIDATION_POLICY,
    onRetryValidationPolicy = () => {},
    showAutoPromotions = false,
    onGiftItemsChange,
    onFreeItemsChange,
    onAppliedPromotionIdsChange,
    persistCustomerOnSave = false,
    savedRedemption,
    loyaltyEditable = true,
    orderCustomerId,
  },
  ref,
) {
  const validationRequirements = customerValidationRequirements(validationPolicy);
  const queryClient = useQueryClient();
  // TCR-1696 — BE đã gác toàn bộ nhóm `/pos/promotions/*` bằng key này (ảnh QA đính kèm cho
  // thấy promotions/vouchers/evaluate cùng trả 403), nhưng icon quà vẫn hiện nên thu ngân bấm
  // vào chỉ nhận modal rỗng. Ẩn icon để UI nói đúng thứ BE đang cho phép.
  const { hasPermission } = useRbac();
  // TCR-1818 — cảnh báo (không chặn) khi chọn khách B2B đang vượt hạn mức công nợ THẬT
  // (`is_over_limit`, cùng khái niệm BE dùng để gắn `credit_approval_status` lúc tạo đơn
  // — xem TCR-1522/TCR-1823), khác `status`/`ratio` ở trang Receivables (so với TB 3 tháng).
  const overLimitCustomerIds = useB2BCreditOverLimitCustomers();
  const canViewPosPromotions = hasPermission('pos.promotions.view');
  // TCR-1697 — chiết khấu tích luỹ B2B tách khỏi `pos.promotions.view` (khuyến mãi/voucher
  // bán lẻ B2C). BE gác route rebate bằng đúng key này, ẩn nút để không mời gọi một thao
  // tác chắc chắn 403.
  const canApplyB2bTradeDiscount = hasPermission('pos.b2b_trade_discount.apply');
  // TCR-1726 — modal "Thêm khách hàng" mở từ nút "+" này POST thẳng /v1/customers (cùng endpoint
  // với nút ở màn /customers), nên nó phải theo cùng một key. NVBH B2B bị bỏ tick "Tạo khách
  // hàng" vẫn tạo được khách ngay trong POS vì nút này trước đây không có gate nào.
  const canCreateCustomer = hasPermission('customers.create');
  // TCR-1952/TCR-1953 — quyền sửa HỒ SƠ khách (khác hẳn `customers.create` ngay trên: tạo khách
  // mới từ nút "+"). Dùng chung một định nghĩa với màn /customers để hai nơi không lệch nhau.
  const customerAbilities = customerEditAbilities(hasPermission);
  const userType = (Form.useWatch('user_type', form) as UserType) ?? 'personal';
  const vouchers = (Form.useWatch('vouchers', form) as VoucherItem[] | undefined) ?? [];
  // TCR-1298 — derive lại (KHÔNG set inline ở từng chỗ sửa `vouchers`): có tới
  // 6 nơi ghi `vouchers` rải rác giữa CustomerSidebar/POS/index.tsx (thêm, gỡ
  // qua nút "x", xoá khách, prune theo giỏ hàng...) — set thủ công từng chỗ dễ
  // sót 1 nơi, để lại `has_freeship_voucher` cũ dính vào đơn kế tiếp. Effect
  // này là NGUỒN DUY NHẤT, tự chạy lại mỗi khi `vouchers` đổi bất kể đổi từ
  // đâu, cùng cách `shipping_discount` tự đồng bộ theo `evaluateQuery` bên dưới.
  useEffect(() => {
    form.setFieldsValue({ has_freeship_voucher: hasFreeshipVoucher(vouchers) });
  }, [vouchers, form]);
  const tradeDiscount = (Form.useWatch('trade_discount', form) as number | undefined) ?? 0;
  const tradeDiscountNote = Form.useWatch('trade_discount_note', form) as string | undefined;
  const loyaltyRedeemPoints = Form.useWatch('loyalty_redeem_points', form) as number | undefined;
  const loyaltyRedeemValue = Form.useWatch('loyalty_redeem_value', form) as number | undefined;
  const customerPhone = Form.useWatch('customer_phone', form) as string | undefined;
  const customerUserId = Form.useWatch('customer_user_id', form) as string | undefined;
  // Khách tổ chức/B2B nhiều trường hợp không có SĐT (`customer_phone` rỗng) —
  // chỉ xét theo SĐT khiến `hasCustomer` sai thành `false` dù đơn đã gắn đúng
  // hồ sơ khách (`customer_user_id`), làm thẻ khách biến mất trên màn sửa đơn.
  const hasCustomer = !!(customerPhone || customerUserId);
  const cityId = Form.useWatch('customer_city_id', form) as string | undefined;

  const [savingCustomer, setSavingCustomer] = useState(false);
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [tradeDiscountModalOpen, setTradeDiscountModalOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [activeKeys, setActiveKeys] = useState<string[]>(['delivery']);

  // Đặt ở effect (không nhét vào từng chỗ gọi `setDialogOpen`) để mọi đường mở
  // dialog — imperative handle, nút "+", nút sửa — đều báo ra ngoài như nhau.
  useEffect(() => {
    onDialogOpenChange?.(dialogOpen);
  }, [dialogOpen, onDialogOpenChange]);

  // Default values once (mount only).
  useEffect(() => {
    form.setFieldsValue({
      user_type: form.getFieldValue('user_type') ?? 'personal',
      invoice_same_as_delivery: form.getFieldValue('invoice_same_as_delivery') ?? false,
      receive_method: form.getFieldValue('receive_method') ?? 'delivery',
      delivery_same_as_customer: form.getFieldValue('delivery_same_as_customer') ?? true,
    });
  }, []);

  /**
   * Trạng thái form để nút "Huỷ" của CustomerDialog khôi phục về.
   *
   * PHẢI chụp TRƯỚC khi ghi bất cứ thứ gì vào form. `handleCustomerSelect` (và
   * `openAdd`) sửa form RỒI mới `setDialogOpen(true)`, nên ảnh chụp lấy tại thời
   * điểm dialog mở đã là trạng thái SAU khi sửa — Huỷ khôi phục lại đúng thứ vừa
   * ghi, tức không huỷ được gì. Đó chính là bug "bấm Huỷ mà khách vẫn được chọn".
   *
   * Vì lý do đó, quyền chụp/khôi phục nằm ở đây chứ không nằm trong CustomerDialog:
   * chỉ nơi gọi mới biết cái gì có trước lần sửa của chính nó.
   */
  const cancelSnapshotRef = useRef<Record<string, unknown> | null>(null);

  function captureCancelSnapshot() {
    const snap: Record<string, unknown> = {};
    // `CANCEL_SNAPSHOT_FIELDS`, KHÔNG phải `CUSTOMER_DIALOG_FIELDS`: nhóm field
    // của thanh phương thức nhận hàng nằm NGOÀI dialog (thanh neo đáy cột hàng,
    // bấm được cả trong lúc dialog mở). Chụp rồi hoàn nguyên chúng nghĩa là nút
    // "Huỷ" của dialog âm thầm bỏ luôn phương thức nhận hàng thu ngân vừa chọn.
    for (const k of CANCEL_SNAPSHOT_FIELDS) snap[k] = form.getFieldValue(k);
    // Hai field điểm tích luỹ KHÔNG nằm trong `CUSTOMER_DIALOG_FIELDS` (xem
    // comment tại nơi khai báo constant) nhưng `handleCustomerSelect` có xoá
    // chúng, nên phải nằm trong ảnh chụp thì Huỷ mới hoàn nguyên đủ.
    snap['loyalty_redeem_points'] = form.getFieldValue('loyalty_redeem_points');
    snap['loyalty_redeem_value'] = form.getFieldValue('loyalty_redeem_value');
    cancelSnapshotRef.current = snap;
  }

  function cancelDialog() {
    if (cancelSnapshotRef.current) form.setFieldsValue(cancelSnapshotRef.current);
    cancelSnapshotRef.current = null;
    setDialogOpen(false);
  }

  useImperativeHandle(ref, () => ({
    openCustomerDialog: (mode: 'add' | 'edit' = 'edit') => {
      if (variant === 'inline') {
        setActiveKeys(['delivery', 'invoice']);
        return;
      }
      captureCancelSnapshot();
      setDialogMode(mode);
      setDialogOpen(true);
    },
  }));

  function handleUserTypeChange(next: UserType) {
    const reset: Record<string, unknown> = {};
    for (const k of FIELDS_RESET_ON_USER_TYPE_CHANGE) {
      reset[k] = k === 'invoice_same_as_delivery' ? false : undefined;
    }
    reset['_cityName'] = undefined;
    reset['_wardName'] = undefined;
    reset['user_type'] = next;
    if (next !== 'organization') {
      reset['trade_discount'] = 0;
      reset['trade_discount_note'] = undefined;
    }
    form.setFieldsValue(reset);
  }

  function openAdd() {
    // Chụp TRƯỚC khi xoá trắng form — KHÔNG phải để "trả lại khách đang chọn"
    // (nút "+" giờ CHỈ hiện khi `!hasCustomer`, nên không có khách nào đang
    // chọn tại thời điểm bấm để mà trả lại). Lý do thật: dialog "Thêm khách
    // hàng" mở ra ngay sau đây cho thu ngân gõ MỚI vào đúng nhóm field trong
    // `CANCEL_SNAPSHOT_FIELDS` (tên, SĐT, địa chỉ, thông tin giao nhận, hoá
    // đơn...). Nếu thu ngân gõ dở rồi bấm "Huỷ", `cancelDialog()` cần ảnh chụp
    // này để khôi phục — thiếu nó, `cancelSnapshotRef.current` là `null` nên
    // "Huỷ" không dọn gì cả, dữ liệu gõ dở ở lại form và rơi vào đơn kế tiếp mà
    // không có gì trên màn hình báo.
    captureCancelSnapshot();
    const reset: Record<string, unknown> = {};
    // Xoá trắng ĐÚNG nhóm field mà ảnh chụp ở trên có: bấm "+" rồi Huỷ phải trả
    // lại nguyên trạng. Xoá cả nhóm của thanh phương thức nhận hàng (nhóm KHÔNG
    // nằm trong ảnh chụp) là mất luôn phương thức nhận hàng, không có đường lùi.
    for (const k of CANCEL_SNAPSHOT_FIELDS) {
      // `description`/`care_staff_id` đã chuyển ra field độc lập ở cột phải
      // (`CustomerNoteField`/`CareStaffAsyncSelect`), giống hệt lý do
      // `clearCustomer()` đã loại trừ hai field này — bấm "+" để thêm khách mới
      // không nên xoá luôn ghi chú/nhân viên chăm sóc đang gõ dở cho đơn.
      if (k === 'description') continue;
      else if (k === 'care_staff_id') continue;
      else if (k === 'invoice_same_as_delivery') reset[k] = false;
      else if (k === 'delivery_same_as_customer') reset[k] = true;
      else reset[k] = undefined;
    }
    reset['user_type'] = userType;
    form.setFieldsValue(reset);
    setDialogMode('add');
    setDialogOpen(true);
  }

  function openEdit() {
    captureCancelSnapshot();
    setDialogMode('edit');
    setDialogOpen(true);
  }

  function clearCustomer() {
    const reset: Record<string, unknown> = {};
    // Ở ĐÂY phải là `CUSTOMER_DIALOG_FIELDS` (gồm cả nhóm vận chuyển), khác hai
    // vòng lặp snapshot ở trên: "xoá khách" là bắt đầu lại từ đầu. Dùng
    // `CANCEL_SNAPSHOT_FIELDS` ⇒ phí ship / hãng / khối lượng gói của khách A
    // sống sót và rơi vào đơn kế tiếp.
    for (const k of CUSTOMER_DIALOG_FIELDS) {
      // Ghi chú giờ là field độc lập ở cột phải (CustomerNoteField), không còn
      // riêng của CustomerDialog — xoá khách không nên xoá luôn ghi chú đơn.
      if (k === 'description') continue;
      // Final review fix I1: Nhân viên chăm sóc (Task 7) cũng đã chuyển ra field
      // độc lập ở cột phải (`CareStaffAsyncSelect` trong `POSPage`/`Orders/edit`),
      // giống hệt `description` — cùng một lớp lỗi nếu bỏ sót ngoại lệ này: xoá
      // khách không nên xoá luôn nhân viên chăm sóc đã chọn cho đơn.
      else if (k === 'care_staff_id') continue;
      else if (k === 'invoice_same_as_delivery') reset[k] = false;
      else if (k === 'delivery_same_as_customer') reset[k] = true;
      else if (k === 'receive_method') reset[k] = 'delivery';
      else reset[k] = undefined;
    }
    // `loyalty_redeem_points`/`loyalty_redeem_value` KHÔNG nằm trong
    // `CUSTOMER_DIALOG_FIELDS` (chúng không do CustomerDialog quản lý — xem
    // comment tại nơi khai báo constant). Không xoá tường minh ở đây thì điểm
    // đã đổi của khách A sống sót qua nút "xoá khách" và rơi vào đơn hàng
    // không-khách kế tiếp, âm thầm trừ vào `summary.total`/COD.
    reset['loyalty_redeem_points'] = undefined;
    reset['loyalty_redeem_value'] = undefined;
    // `vouchers`/`voucher_discount` cùng một lớp lỗi với loyalty ở trên (cũng
    // không nằm trong `CUSTOMER_DIALOG_FIELDS`) — thiếu 2 dòng này thì voucher
    // đã áp cho khách A sống sót qua nút "xoá khách", rơi vào đơn không-khách
    // kế tiếp và vẫn trừ tiền dù không còn khách nào gắn với đơn.
    reset['vouchers'] = [];
    reset['voucher_discount'] = 0;
    form.setFieldsValue(reset);
    // Đối xứng với nút "x" gỡ 1 voucher (xem `onGiftItemsChange` phía dưới) —
    // xoá khách xoá TOÀN BỘ voucher nên cũng phải gỡ toàn bộ dòng quà 0đ gắn
    // với các voucher đó, không chỉ dòng quà của 1 voucher. Thiếu bước này để
    // lại dòng quà mồ côi trong giỏ giống hệt bug đã note ở dòng ~983.
    //
    // Fix round 1, M1 (cùng ổ với C1): trước đây gọi
    // `onGiftItemsChange?.(cartItems.filter(...))` — truyền TOÀN BỘ giỏ (trừ
    // đúng các dòng quà vừa gỡ) thay vì CHỈ danh sách quà VOUCHER còn lại. Vi
    // phạm hợp đồng "giftItems = danh sách quà voucher còn lại" của
    // `onGiftItemsChange` (xem doc comment ở khai báo prop) — caller nối
    // `giftItems` này vào `prev` đã lọc quà voucher, khiến MỌI dòng hàng
    // thường (và cả quà CTKM) bị nhân đôi, cùng lớp lỗi crash `computePosMoney`
    // như C1. `vouchers` bị xoá TOÀN BỘ ở đây nên danh sách quà voucher còn lại
    // luôn RỖNG — không cần giữ lại phần tử nào.
    const clearedCodes = new Set(vouchers.map((v) => v.code));
    if (clearedCodes.size > 0) {
      onGiftItemsChange?.(
        cartItems.filter(
          (i) =>
            isVoucherGiftItem(i) &&
            i.gift_promotion_id &&
            !clearedCodes.has(i.gift_promotion_id),
        ),
      );
    }
  }

  // TCR-1818 — reactive theo `customerUserId`: Tag đỏ ở `SelectedCustomerCard` phải còn
  // hiển thị liên tục (khác pop-up cảnh báo bên dưới, chỉ bật tại các mốc cụ thể).
  // `customerUserId` đã khai báo ở đầu component (dùng chung cho `hasCustomer`).
  const isOverLimitCustomer = Boolean(customerUserId) && overLimitCustomerIds.has(customerUserId as string);

  // TCR-1818 (fix vòng 3) — PO đánh FAILED bản cảnh báo dạng banner/text (đầu dialog +
  // thẻ khách) vì "lẫn vào các dòng, khó để ý", yêu cầu quay lại đúng 1 pop-up riêng lẻ
  // (kiểu "Cảnh báo vi phạm" ở bản v1). `shownOverLimitWarningRef` chống pop-up bật lại
  // liên tục mỗi lần re-render trong khi `isOverLimitCustomer` vẫn true (vd gõ giỏ hàng) —
  // chỉ bật lại khi ĐỔI sang một khách vượt hạn mức KHÁC.
  const [overLimitWarningCustomerId, setOverLimitWarningCustomerId] = useState<string | null>(null);
  const shownOverLimitWarningRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOverLimitCustomer || !customerUserId) return;
    if (shownOverLimitWarningRef.current.has(customerUserId)) return;
    shownOverLimitWarningRef.current.add(customerUserId);
    setOverLimitWarningCustomerId(customerUserId);
  }, [isOverLimitCustomer, customerUserId]);

  // Mở lại dialog sửa khách (mode 'edit') cho đúng khách đang vượt hạn mức cũng bật lại
  // pop-up — saler dễ quên cảnh báo cũ nếu quay lại sửa hồ sơ khách sau một lúc thao tác
  // khác. Chỉ phụ thuộc `dialogOpen` (không phụ thuộc `dialogMode`/`isOverLimitCustomer`)
  // để tránh bật lặp lại khi các giá trị đó đổi trong lúc dialog đang mở.
  useEffect(() => {
    if (dialogOpen && dialogMode === 'edit' && isOverLimitCustomer && customerUserId) {
      setOverLimitWarningCustomerId(customerUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  function reopenOverLimitWarning() {
    if (customerUserId) setOverLimitWarningCustomerId(customerUserId);
  }

  // TCR-1069: đơn đủ điều kiện KM tự động (khác voucher nhập tay) trước đây
  // không hiện gì ở đây — `VoucherModal` biết CTKM nào "Tự động áp dụng" nhưng
  // loại nó khỏi `onConfirm` (checkbox bị disable), nên không có đường nào đưa
  // dữ liệu đó ra ngoài. Đánh giá lại true eligibility bằng chính engine BE
  // dùng lúc tạo đơn (`/pos/promotions/evaluate`), debounce theo giỏ hàng để không
  // gọi API mỗi phím gõ.
  // review round 3 (finding A): `showAutoPromotions` false ⇒ trả `null` vô
  // điều kiện, KHÔNG đọc `cartItems`/`customerUserId`/`selectedStoreId` —
  // `useDebounce`/`usePosEvaluateCart` bên dưới thấy payload `null` thì
  // `enabled: false`, không bắn request nào. Đây là cách duy nhất "tắt" một
  // network call phát sinh từ query hook mà không được gọi hook có điều kiện.
  // `preserve: true` là BẮT BUỘC ở cả hai watch dưới: hai `Form.Item` này do
  // `FulfillmentFormSection` sở hữu và chỉ mount khi khối vận chuyển hiển thị.
  // Thiếu nó, `useWatch` trả `undefined` ở mọi màn không render khối đó — với
  // `receive_method` là hàng rào điểm tích luỹ im lặng mở lại cho pickup/vault,
  // với `shipping_fee` là CTKM freeship không bao giờ chấm ra tiền vì BE nhận
  // phí bằng 0.
  const receiveMethod =
    (Form.useWatch('receive_method', { form, preserve: true }) as
      | 'delivery'
      | 'pickup'
      | 'vault'
      | undefined) ?? 'delivery';
  const shippingFeeWatched = Form.useWatch('shipping_fee', { form, preserve: true }) as
    | number
    | string
    | undefined;
  const shippingFee = Number(shippingFeeWatched ?? 0) || 0;

  const evaluatePayload = useMemo(() => {
    if (!showAutoPromotions || cartItems.length === 0) return null;
    const items = buildEvaluateItems(cartItems);
    // Giỏ chỉ còn dòng quà ⇒ không còn gì để chấm điều kiện.
    if (items.length === 0) return null;
    return {
      items,
      customer_id: customerUserId,
      store_id: selectedStoreId,
      channel: 'pos' as const,
      payment_method: paymentMethod,
      shipping_fee: shippingFee,
      fulfillment_mode: receiveMethod,
    };
  }, [
    showAutoPromotions,
    cartItems,
    customerUserId,
    selectedStoreId,
    paymentMethod,
    shippingFee,
    receiveMethod,
  ]);
  const debouncedEvaluatePayload = useDebounce(evaluatePayload, 500);
  const evaluateQuery = usePosEvaluateCart(debouncedEvaluatePayload);
  const evaluationPayloadKey = evaluatePayload ? JSON.stringify(evaluatePayload) : null;
  const debouncedEvaluationPayloadKey = debouncedEvaluatePayload
    ? JSON.stringify(debouncedEvaluatePayload)
    : null;
  const [appliedEvaluationPayloadKey, setAppliedEvaluationPayloadKey] =
    useState<string | null>(null);
  const promotionEvaluationFresh =
    evaluationPayloadKey !== null &&
    appliedEvaluationPayloadKey === evaluationPayloadKey;
  const appliedPromotions = promotionEvaluationFresh
    ? evaluateQuery.data?.applied_promotions ?? []
    : [];
  const promotionDiscount = useMemo(
    // TCR-1143 (fix double-discount, findings #2/#3 final review): dòng flash
    // sale trong giỏ đã mang GIÁ FLASH (đã giảm sẵn) — xem `addFlashSaleItem`
    // ở `POS/index.tsx`. Khoản `discount_amount` mà BE trả về cho entry
    // `type: 'flash_sale'` chỉ để HIỂN THỊ (số tiền đã giảm so với giá gốc),
    // KHÔNG phải một khoản giảm giá CỘNG THÊM cần trừ lần nữa khỏi
    // `totalGoods`. Nếu cộng cả khoản này vào `promotionDiscount` thì
    // `computePosMoney()` (tcr-erp-shared) sẽ trừ giá flash sale hai lần —
    // một lần ẩn trong `item.price`, một lần ở đây — khiến `taxable
    // base`/VAT/`final_total` bị floor về 0 khi tổng discount vượt quá
    // `totalGoods` (xem `Math.min(...)` clamp trong `compute.ts`). Đây là bug
    // đã fix ở BE (TCR-1143 C1, 2026-08-17): `promotion-engine.service.ts#evaluate()`
    // seed `otherPromotionsDiscount` ở 0 thay vì `flashSaleResult.discount`,
    // nên `total_discount`/`final_total` BE trả về giờ KHÔNG còn cộng dồn
    // flash discount nữa; áp dụng cùng nguyên tắc ở FE.
    //
    // Rút ra `sumNonFlashPromotionDiscount` (`@/utils/promotionDiscount`) —
    // CÙNG bug từng bị vá copy-tay riêng lẻ ở đây (2 call site) và còn
    // nguyên, chưa vá, ở `Orders/detail.tsx`/`OrderSummaryModal.tsx` (đọc
    // cùng field `applied_promotions` từ đơn đã lưu). Dùng chung một hàm để
    // không còn N bản copy-tay dễ lệch nhau.
    () => sumNonFlashPromotionDiscount(appliedPromotions),
    [appliedPromotions],
  );

  useEffect(() => {
    if (evaluateQuery.error) {
      console.warn('Không đánh giá được khuyến mãi tự động cho giỏ hàng', evaluateQuery.error);
    }
  }, [evaluateQuery.error]);

  useEffect(() => {
    form.setFieldsValue({ promotion_discount: promotionDiscount });
  }, [promotionDiscount, form]);

  // Freeship: khoản giảm PHÍ VẬN CHUYỂN, KHÔNG gộp vào `promotion_discount`.
  // Hai trục tiền khác nhau — `promotion_discount` trừ vào tiền hàng và hạ cơ
  // sở tính thuế, còn khoản này trừ ở bước cộng phí ship. Gộp là hạ VAT hàng
  // hoá bằng một khoản chẳng liên quan gì tới hàng hoá.
  const shippingDiscount = promotionEvaluationFresh
    ? (evaluateQuery.data?.shipping_discount ?? 0)
    : 0;
  useEffect(() => {
    form.setFieldsValue({ shipping_discount: shippingDiscount });
  }, [shippingDiscount, form]);

  // Task 6: báo `free_items` ra ngoài cho POSPage/OrderEditPage render banner
  // "đơn đủ điều kiện nhận quà" + tự thêm dòng quà 0đ vào giỏ.
  const freeItems = promotionEvaluationFresh ? (evaluateQuery.data?.free_items ?? []) : [];
  // `freeItemsKey` thay cho `freeItems`: mảng mới toanh mỗi lần render (kể cả
  // khi nội dung không đổi) sẽ làm effect chạy vô hạn nếu caller setState
  // trong `onFreeItemsChange` (đúng thứ cả `POSPage` lẫn `OrderEditPage` làm).
  // Cùng kỹ thuật `JSON.stringify` đã dùng cho `evaluationPayloadKey` ở trên.
  const freeItemsKey = JSON.stringify(freeItems);
  useEffect(() => {
    onFreeItemsChange?.(freeItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeItemsKey, onFreeItemsChange]);

  // TCR-1403: báo tập promotion_id còn hợp lệ ra ngoài để cascade-gỡ quà CTKM
  // mồ côi (xem doc comment `onAppliedPromotionIdsChange` + `usePromotionGiftAdd`).
  const appliedPromotionIds = resolveAppliedPromotionIds({
    showAutoPromotions,
    hasEvaluableCartItems: evaluatePayload !== null,
    promotionEvaluationFresh,
    appliedPromotions,
  });
  const appliedPromotionIdsKey = appliedPromotionIds ? JSON.stringify(appliedPromotionIds) : null;
  useEffect(() => {
    if (appliedPromotionIds === null) return;
    onAppliedPromotionIdsChange?.(appliedPromotionIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPromotionIdsKey, onAppliedPromotionIdsChange]);

  useEffect(() => {
    // Invalidate immediately, before the 500ms debounce starts. The previous
    // snapshot must never remain payable while the raw cart is dirty.
    setAppliedEvaluationPayloadKey(null);
    form.setFieldsValue({
      promotion_snapshot_id: undefined,
      promotion_discount: 0,
      shipping_discount: 0,
      promotion_evaluating: evaluationPayloadKey !== null,
    });
  }, [evaluationPayloadKey, form]);

  useEffect(() => {
    if (
      !evaluateQuery.data ||
      evaluateQuery.isFetching ||
      debouncedEvaluationPayloadKey === null ||
      debouncedEvaluationPayloadKey !== evaluationPayloadKey
    ) {
      return;
    }
    setAppliedEvaluationPayloadKey(debouncedEvaluationPayloadKey);
    form.setFieldsValue({
      promotion_snapshot_id: evaluateQuery.data.snapshot_id,
      // TCR-1143 (fix double-discount): giữ đồng bộ với `promotionDiscount`
      // useMemo ở trên — cùng dùng `sumNonFlashPromotionDiscount` để loại
      // entry `type: 'flash_sale'` khỏi tổng vì giá flash đã nằm sẵn trong
      // `item.price` của dòng giỏ hàng, không phải khoản giảm cộng thêm. Xem
      // comment đầy đủ ở định nghĩa `promotionDiscount` phía trên.
      promotion_discount: sumNonFlashPromotionDiscount(evaluateQuery.data.applied_promotions),
      shipping_discount: evaluateQuery.data.shipping_discount ?? 0,
    });
  }, [
    debouncedEvaluationPayloadKey,
    appliedEvaluationPayloadKey,
    evaluateQuery.data,
    evaluateQuery.isFetching,
    evaluationPayloadKey,
    form,
  ]);

  // review round 3 (finding C): giỏ debounce 500ms trước khi gọi API, rồi còn
  // round-trip mạng nữa — có một khoảng thu ngân bấm "Ghi hoá đơn" trong lúc
  // `promotion_discount` trên màn hình chưa phải số cuối cùng. Ghi cờ này vào
  // form để `POSPage.handleCheckout` chặn submit giống hệt cổng voucher
  // revalidate, KHÔNG để đơn ghi với khuyến mãi chưa chốt.
  useEffect(() => {
    form.setFieldsValue({
      promotion_evaluating: evaluateQuery.isFetching || !promotionEvaluationFresh,
    });
  }, [evaluateQuery.isFetching, form, promotionEvaluationFresh]);

  // ── Điểm tích luỹ: mở cho GIAO HÀNG + LẤY TẠI CHI NHÁNH, KHÔNG mở cho B2B ──
  //
  // TCR-1365 — `pickup` được đưa vào danh sách hợp lệ. Hàng rào TCR-1253 mô tả
  // ngay dưới đây vẫn đúng NGUYÊN VĂN cho `vault` và khách B2B; riêng đơn lấy
  // tại chi nhánh thì BE đã đổi luật: `pos-order-payment.service.ts` tính
  // `loyaltyEligible = !isB2B && (hasShipping || pickup)` và trừ khoản điểm
  // THẬT vào `money.customerPayable` (số thu ngân thu tại quầy), đồng thời
  // `pos-order-promotion.service.ts` trừ điểm khỏi sổ khách theo CÙNG cờ đó.
  // Hai vế "giảm tiền" và "trừ điểm" vì vậy vẫn đi cùng nhau — điều kiện duy
  // nhất mà TCR-1253 thực sự bảo vệ.
  //
  // ⚠️ Cần BE (`loyaltyEligible`) đã deploy trước FE này: BE cũ zero khoản
  // giảm trong khi FE mới hiện số đã giảm ⇒ guard `CASH_AMOUNT_MISMATCH` phía
  // BE sẽ ném 400 (không ghi sai tiền, nhưng thu ngân không tạo được đơn).
  //
  // ── Nguyên văn hàng rào cũ (còn hiệu lực cho vault + B2B) ─────────────────
  //
  // BE chỉ trừ `loyalty_redeem_value` khỏi COD/`summary.total` ở nhánh giao hàng
  // CÁ NHÂN (`pos-order-payment.service.ts`: `loyalty: isB2BPosOrder ||
  // !hasShipping ? 0 : ...` — B2B bị loại khỏi điểm tích luỹ ở MỌI nơi khác
  // trong hệ thống, xem `buildPosMoneyInput.ts` phía FE dùng chung công thức).
  // Đơn nhận tại quầy / gửi kho / khách B2B dùng điểm sẽ bị ĐỐT điểm mà khách
  // không được giảm một đồng nào. TCR-1253 (comment Phạm Duy Anh 19/08): trước
  // bản sửa này, badge + popover "Dùng điểm" chỉ khoá theo `receiveMethod`,
  // KHÔNG khoá theo `userType` — khách B2B giao hàng vẫn đổi điểm được, thấy
  // "Đã dùng X điểm = Yđ" và dòng "Điểm tích luỹ quy đổi -Yđ" trên
  // `OrderInfoPanel`, nhưng `buildPosMoneyInput` (isB2B) zero khoản đó ra khỏi
  // `goodsTaxableAmount`/`customerPayable` — số hiển thị nói giảm, số thực thu
  // không giảm. Trong lúc chờ quyết định nghiệp vụ cho đơn tại quầy/B2B, khoá
  // hẳn ô đổi điểm ở các trường hợp đó — thà không mời còn hơn mời rồi mất điểm.
  // `preserve: true` là BẮT BUỘC: `Form.Item name="receive_method"` do
  // `FulfillmentFormSection` sở hữu và chỉ mount khi khối vận chuyển hiển thị.
  // Thiếu nó, `useWatch` trả `undefined` ở mọi màn không render khối đó và cả
  // hàng rào này im lặng mở lại cho pickup/vault.
  const loyaltyEnabled =
    (receiveMethod === 'delivery' || receiveMethod === 'pickup') &&
    userType !== 'organization';

  // Đổi sang vault (TCR-1365: KHÔNG còn tính pickup), hoặc đổi loại khách sang
  // Tổ chức, SAU khi đã đổi điểm: phải xoá giá trị khỏi form. Bỏ treo ở đó thì
  // `loyalty_redeem_value` vẫn đi kèm payload lên BE và âm thầm hạ
  // `summary.total` — đúng khoản tiền mà thu ngân không hề thấy trên màn hình.
  // Ngược lại, chuyển sang `pickup` giờ GIỮ nguyên điểm đã đổi: đơn vẫn đủ tư
  // cách dùng điểm, xoá đi là bắt thu ngân đổi lại và làm số tiền nhảy không
  // lý do.
  useEffect(() => {
    if (loyaltyEnabled) return;
    if (
      form.getFieldValue('loyalty_redeem_points') === undefined &&
      form.getFieldValue('loyalty_redeem_value') === undefined
    ) {
      return;
    }
    form.setFieldsValue({
      loyalty_redeem_points: undefined,
      loyalty_redeem_value: undefined,
    });
  }, [loyaltyEnabled, form]);

  // Thẻ điểm tích lũy — dùng chung: nhúng vào card (compact) hoặc đứng riêng (inline).
  //
  // TCR-1353: khoản điểm ĐÃ CHỐT trên đơn (`savedRedemption`, màn sửa đơn
  // truyền vào) phải hiện kể cả khi `loyaltyEnabled` đang tắt (vd đơn đang sửa
  // đã đổi sang nhận tại chi nhánh) — hàng rào đó chỉ chặn việc ĐỔI điểm MỚI,
  // không phải việc hiển thị khoản đã đổi. Giấu đi thì người dùng không hiểu
  // vì sao tiền đơn lại như vậy.
  //
  // Review fix (Critical): nút "Dùng điểm" KHÔNG được tách khỏi `loyaltyEnabled`.
  // Nếu chỉ xét `loyaltyEditable`, nhân viên có thể mở đơn đã chốt điểm
  // (savedRedemption có sẵn), đổi sang pickup/B2B (loyaltyEnabled → false,
  // effect ở trên đã xoá field form), rồi vẫn bấm "Dùng điểm" và ghi đè điểm
  // mới lên đúng đơn mà TCR-1253 định chặn. `loyaltyEnabled` PHẢI luôn nằm
  // trong điều kiện cho phép đổi — `loyaltyEditable` chỉ được NỚI THÊM khi
  // gate đó đã mở, không bao giờ thay thế nó.
  //
  // Re-review (Important) — BỎ HẲN nhánh `savedRedemption ? ... : true`.
  // Nhánh `: true` từng cho đơn CHƯA từng dùng điểm (`savedRedemption`
  // `undefined`) bấm được "Dùng điểm" bất kể `loyaltyEditable` — đúng lỗ mà
  // vá C1 (hydrate `customer_user_id`, khiến badge từ "không bao giờ render"
  // sang "render thật") phơi ra: đơn `confirmed`/`delivering` (KHÔNG
  // `isFullyEditable`), giao hàng, khách cá nhân, CHƯA từng dùng điểm vẫn mở
  // được popover. Bấm xác nhận → `loyaltyPointsChangedForPreview` (ở
  // `edit.tsx`, đã gate `isFullyEditable &&` ngay trên) chặn preview đúng,
  // nhưng nếu chỉ vá MỘT phía preview thì người dùng vẫn thao tác được vào
  // một đường chắc chắn dẫn tới 400/nuốt âm thầm ở BE khi lưu — sửa ở GỐC
  // (không cho MỞ nút) tốt hơn vì chặn NGAY TỪ ĐẦU, không để người dùng bước
  // vào rồi mới hứng hậu quả.
  //
  // `editable={loyaltyEditable && loyaltyEnabled}` — ĐÚNG cho mọi tổ hợp đã
  // kiểm tay:
  //  - POS tạo đơn: `loyaltyEditable` mặc định `true`, badge chỉ render khi
  //    `loyaltyEnabled` đã `true` ⇒ `true && true` = `true`, KHÔNG đổi hành vi.
  //  - Sửa đơn, đơn sửa được, có điểm, giao hàng ⇒ `true`.
  //  - Sửa đơn, đơn sửa được, có điểm, đổi sang lấy tại chi nhánh ⇒ `false`
  //    (hàng rào TCR-1253 giữ nguyên).
  //  - Sửa đơn, đơn KHÔNG sửa được ⇒ `false` bất kể có điểm hay không — lỗ
  //    đang bịt.
  //  - Sửa đơn, đơn sửa được, CHƯA từng dùng điểm ⇒ `true`, người dùng thêm
  //    điểm được — đúng mong muốn.
  //
  // FINAL-I1 (final review) — `customerId` của BADGE (chỉ badge, KHÔNG phải
  // `customerUserId` dùng chung cho voucher/KM/chiết khấu) PHẢI khoá về
  // `orderCustomerId` (= `order.user_id`, `edit.tsx` truyền) khi có, thay vì
  // đọc thẳng khách ĐANG chọn trên form. Xem doc-comment đầy đủ ở
  // `orderCustomerId` trong `Props` — tóm tắt: payload PATCH không gửi
  // `customer_id`, nên nếu nhân viên lỡ đổi sang khách B rồi đổi điểm, số dư
  // hiện ra là của B nhưng điểm bị TRỪ NHẦM vào sổ của khách A (chủ thật của
  // đơn) khi lưu. Khoá cứng về A ngay từ khâu hiển thị/redeem — không đợi
  // request 400 hay sai lệch sổ mới phát hiện. `POS/index.tsx` không truyền
  // `orderCustomerId` ⇒ rơi về `customerUserId` như cũ, không đổi hành vi.
  // `customerUserId as string`: TS không tự narrow qua biến trung gian
  // `loyaltyCustomerId` dù nhánh JSX này chỉ render khi `customerUserId &&
  // (...)` đã đúng ở điều kiện ternary bên dưới — ép kiểu AN TOÀN vì đã có
  // gate runtime đó bảo đảm, không phải bỏ qua kiểm tra.
  const loyaltyCustomerId = orderCustomerId ?? (customerUserId as string);
  const loyaltyBadge = customerUserId && (loyaltyEnabled || savedRedemption) ? (
    <LoyaltyBadge
      customerId={loyaltyCustomerId}
      mode={savedRedemption ? 'edit' : 'create'}
      editable={loyaltyEditable && loyaltyEnabled}
      // FINAL-C1 (final review) — số điểm ĐÃ LƯU trên đơn, đứng yên suốt
      // phiên sửa (khác `redeemedPoints` bên dưới, đã đổi nghĩa thành giá
      // trị LIVE) — `LoyaltyBadge` dùng để cộng vào số dư ví, tính trần đổi
      // điểm KHẢ DỤNG thật cho đơn đang sửa (xem doc-comment `heldPoints` ở
      // `LoyaltyBadge.tsx`).
      heldPoints={savedRedemption?.points}
      onRedeem={(result: LoyaltyRedeemResult) => {
        form.setFieldsValue({
          loyalty_redeem_points: result.points,
          loyalty_redeem_value: result.value,
        });
      }}
      /**
       * Review (b) — ƯU TIÊN giá trị LIVE trên form (`loyaltyRedeemPoints`/
       * `loyaltyRedeemValue`, do CHÍNH `onRedeem` ngay trên vừa ghi) trước
       * `savedRedemption`. Thứ tự CŨ (`savedRedemption?.points ?? ...`) làm
       * `savedRedemption` LUÔN thắng ở mode 'edit' — người dùng bấm "Xác nhận
       * dùng điểm"/"Gỡ hết điểm", thấy toast báo thành công, nhưng chip vẫn
       * hiện đúng số CŨ vì `savedRedemption` (prop truyền từ `edit.tsx`, chỉ
       * đổi lại SAU khi lưu + tải lại đơn) không hề đổi trong phiên này —
       * trông như thao tác không có tác dụng gì. `??` chỉ rơi về
       * `savedRedemption` khi field form CHƯA từng được ghi (`undefined`) —
       * đúng lúc mới mở trang, chưa đụng gì tới ô điểm.
       */
      redeemedPoints={loyaltyRedeemPoints ?? savedRedemption?.points}
      redeemedValue={loyaltyRedeemValue ?? savedRedemption?.value}
    />
  ) : null;

  /**
   * TCR-2128 — ghi tab "Thông tin giao nhận" vào Sổ địa chỉ CHỈ KHI thu ngân tick ô "Lưu vào Sổ
   * địa chỉ" (thay cho TCR-1146 vòng 3: luôn ghi đè địa chỉ mặc định mỗi lần Xác nhận — đơn giao
   * một lần tới siêu thị khác làm bẩn địa chỉ mặc định của khách GT).
   *
   * Ô đó chỉ hiện khi đã sửa tay, nên kiểm lại đúng các điều kiện hiển thị: giá trị tick có thể
   * còn sót lại sau khi thu ngân tick rồi sửa ngược về y như cũ (ô ẩn đi nhưng value vẫn `true`).
   *
   * Địa chỉ xuất phát từ 1 entry (`_deliveryAddressSource.address_id`) → cập nhật entry đó;
   * ngược lại → thêm mới (sổ rỗng thì BE tự đặt làm mặc định).
   *
   * Trả `true` khi đã ghi, `false` khi không cần ghi; lỗi mạng ném ra cho caller báo.
   */
  async function saveDeliveryToAddressBook(customerId: string): Promise<boolean> {
    const values = form.getFieldsValue(true) as Record<string, unknown>;
    const source = values._deliveryAddressSource as DeliveryAddressSource | undefined;
    if (values.delivery_same_as_customer) return false;
    const wantsEdit = Boolean(values.delivery_save_to_book) && isDeliveryEdited(values, source);

    // TCR-2384 — thêm người vừa gõ vào "Người nhận khác" của địa chỉ đang áp. Kiểm lại đúng điều
    // kiện hiển thị ô tick (giá trị tick có thể còn sót khi ô đã ẩn).
    const typedName = String(values.delivery_name ?? '').trim();
    const typedPhone = String(values.delivery_phone ?? '').trim();
    const wantsReceiver =
      Boolean(values.delivery_add_receiver_to_book) &&
      Boolean(source?.address_id) &&
      Boolean(typedName) &&
      Boolean(typedPhone) &&
      !matchRecipient(source, typedName, typedPhone);
    if (!wantsEdit && !wantsReceiver) return false;

    let body: Record<string, unknown> | null = wantsEdit ? buildAddressBody(values) : null;
    if (!body && wantsReceiver && source) {
      // Chỉ tick người nhận: body tối thiểu từ bản chụp địa chỉ (BE bắt buộc name/city/ward/address).
      const s = source.snapshot;
      if (s.delivery_name && s.delivery_city_id && s.delivery_ward_id && s.delivery_address) {
        body = {
          name: s.delivery_name,
          city_id: s.delivery_city_id,
          ward_id: s.delivery_ward_id,
          address: s.delivery_address,
        };
      }
    }
    if (!body) {
      if (wantsReceiver) {
        message.warning('Chưa lưu được người nhận vào Sổ địa chỉ: địa chỉ thiếu thông tin bắt buộc');
      }
      return false;
    }

    let nextRecipients = source?.recipients;
    if (wantsReceiver && source) {
      // Người chính giữ nguyên (name/phone của entry), không bị người vừa gõ thay thế.
      const main = source.recipients?.find((r) => r.is_main);
      body.name = main?.name || source.snapshot.delivery_name;
      const mainPhone = main?.phone ?? source.snapshot.delivery_phone;
      if (mainPhone) body.phone = mainPhone;
      else delete body.phone;
      // Danh sách người nhận lấy từ sổ MỚI NHẤT: `source.recipients` chụp lúc áp địa chỉ, nếu
      // Sổ địa chỉ đã sửa thêm người từ đó thì dựng từ bản chụp sẽ xoá mất họ.
      let others: { name: string; phone: string; note?: string }[] = (source.recipients ?? [])
        .filter((r) => !r.is_main)
        .map((r) => ({ name: r.name, phone: r.phone, ...(r.note ? { note: r.note } : {}) }));
      try {
        // Gọi thẳng fetcher dùng chung với `useCustomerAddresses` (không qua cache query: fetchQuery
        // bị huỷ khi observer của modal Sổ địa chỉ gỡ ra giữa chừng).
        const latest = await fetchCustomerAddresses(customerId);
        const entry = (latest as unknown as AddressBookEntry[] | undefined)?.find(
          (e) => e.address_id === source.address_id,
        );
        if (entry) {
          others = (entry.receivers ?? []).map((r) => ({
            name: r.name,
            phone: r.phone,
            ...(r.note ? { note: r.note } : {}),
          }));
        }
      } catch {
        // Lỗi tải sổ: dùng bản chụp lúc áp địa chỉ.
      }
      const typedNorm = normalizePhone(typedPhone);
      const already = others.some((r) => normalizePhone(r.phone) === typedNorm);
      const finalReceivers = already ? others : [...others, { name: typedName, phone: typedPhone }];
      body.receivers = finalReceivers;
      nextRecipients = [
        ...(source.recipients ?? []).filter((r) => r.is_main),
        ...finalReceivers.map((r) => ({ ...r, is_main: false })),
      ];
    }

    let savedId = source?.address_id;
    if (savedId) {
      await customerService.updateAddress(customerId, savedId, body);
    } else {
      const res = await customerService.addAddress(customerId, body);
      const created = res.data as unknown as { address_id?: string; _id?: string } | undefined;
      savedId = created?.address_id ?? created?._id;
    }
    // Sổ đã khớp form: nguồn mới = entry vừa lưu, bỏ tick để lần Xác nhận sau không ghi lại.
    // name/phone của bản chụp = người CHÍNH trong sổ (body), không phải người nhận đang chọn.
    form.setFieldsValue({
      _deliveryAddressSource: {
        address_id: savedId,
        snapshot: {
          ...takeDeliverySnapshot(values),
          delivery_name: String(body.name ?? ''),
          delivery_phone: String(body.phone ?? ''),
        },
        recipients: nextRecipients,
        note: source?.note,
        address_name: source?.address_name,
      } satisfies DeliveryAddressSource,
      delivery_save_to_book: false,
      delivery_add_receiver_to_book: false,
    });
    queryClient.invalidateQueries({ queryKey: customerKeys.addresses(customerId) });
    return true;
  }

  /**
   * TCR-1146: "Xác nhận" trong dialog sửa nhanh.
   *
   * Ngoài việc đóng dialog, còn ghi thông tin vừa sửa vào hồ sơ khách khi
   * `persistCustomerOnSave` bật — đây là yêu cầu của ticket ("lưu trực tiếp
   * ngay tại màn tạo đơn"), vì trước đây dialog chỉ validate rồi đóng nên CCCD
   * gõ vào chỉ sống trong form của đơn đang mở.
   *
   * CHỈ áp dụng cho khách ĐÃ CÓ hồ sơ (`customer_user_id`). Khách mới gõ tay
   * chưa có id — hồ sơ của họ do luồng thanh toán tạo
   * (`pos_auto_sync_customer_enabled` ở `pos-order.service.ts`), tạo thêm ở đây
   * sẽ sinh hồ sơ trùng cho một đơn có thể còn bị huỷ.
   *
   * Lỗi mạng KHÔNG chặn bán hàng: dialog vẫn đóng và dữ liệu vẫn nằm trong đơn,
   * chỉ báo lỗi để thu ngân biết hồ sơ khách chưa được cập nhật.
   */
  async function handleDialogSaved() {
    // Lưu thành công thì bỏ ảnh chụp: giữ lại chỉ chờ ngày một lần Huỷ sau đó
    // nuốt mất thay đổi vừa lưu.
    cancelSnapshotRef.current = null;

    const customerId = form.getFieldValue('customer_user_id') as string | undefined;
    if (!customerId) {
      setDialogOpen(false);
      return;
    }

    // TCR-2128 — ghi Sổ địa chỉ là ý định TƯỜNG MINH của thu ngân (tick ô "Lưu vào Sổ địa chỉ"),
    // nên KHÔNG phụ thuộc `persistCustomerOnSave` như phần hồ sơ khách bên dưới.
    // TCR-1952 — vẫn đòi `customers.delivery_address.update` (hoặc quyền sửa toàn bộ); thiếu quyền
    // ô tick không hiện, nhưng gác lại ở đây cho chắc. Khối giao nhận vẫn đi theo đơn như thường.
    const wantsBookSave =
      customerAbilities.canEditDeliveryAddress &&
      (Boolean(form.getFieldValue('delivery_save_to_book')) ||
        Boolean(form.getFieldValue('delivery_add_receiver_to_book')));

    // TCR-1953 — lọc payload xuống đúng phần được phép ghi TRƯỚC khi gọi API. Trước fix, dialog
    // gửi nguyên hồ sơ bất kể quyền: BE trả 403, `catch` bên dưới nuốt thành toast "Thông tin vẫn
    // được dùng cho đơn này" — thu ngân/NVKD đọc xong vẫn tưởng mình vừa sửa được thông tin khách.
    // Nay thiếu quyền thì KHÔNG gọi mạng nữa, dữ liệu vẫn chảy vào đơn như cũ.
    const profileUpdate = persistCustomerOnSave
      ? pickEditableCustomerFields(
          buildCustomerProfileUpdate(form.getFieldsValue(true), userType),
          customerAbilities,
        )
      : {};
    const hasProfileChanges = Object.keys(profileUpdate).length > 0;
    if (!hasProfileChanges && !wantsBookSave) {
      setDialogOpen(false);
      return;
    }

    setSavingCustomer(true);
    try {
      if (hasProfileChanges) {
        await customerService.update(customerId, profileUpdate);
        // Màn Khách hàng / thẻ khách đọc qua TanStack Query — không dọn cache thì
        // người dùng mở hồ sơ ngay sau đó vẫn thấy dữ liệu cũ, đúng kiểu "sửa mà
        // không thấy đổi" mà ticket này đang đi sửa.
        queryClient.invalidateQueries({ queryKey: customerKeys.all });
        message.success('Đã cập nhật hồ sơ khách hàng');
      }

      // Lỗi ở bước này KHÔNG được đè lên toast "Đã cập nhật hồ sơ khách hàng" — hồ sơ chính đã
      // lưu thành công, sổ địa chỉ báo riêng bằng cảnh báo (thu ngân đã chủ động tick nên phải
      // biết là chưa lưu được, khác TCR-1146 cũ chỉ log).
      if (wantsBookSave) {
        try {
          if (await saveDeliveryToAddressBook(customerId)) {
            message.success('Đã lưu địa chỉ vào Sổ địa chỉ của khách');
          }
        } catch (addrErr) {
          console.error('Không lưu được địa chỉ vào Sổ địa chỉ (Tab Giao nhận)', addrErr);
          const msg = (addrErr as { response?: { data?: { message?: string } } }).response?.data
            ?.message;
          message.warning(
            `Không lưu được vào Sổ địa chỉ${msg ? `: ${msg}` : ''}. Địa chỉ vẫn được dùng cho đơn này.`,
          );
        }
      }
    } catch (e) {
      console.error('Không lưu được hồ sơ khách hàng', e);
      message.error(
        'Không lưu được hồ sơ khách hàng. Thông tin vẫn được dùng cho đơn này — ' +
          'vui lòng cập nhật lại ở màn Khách hàng.',
      );
    } finally {
      setSavingCustomer(false);
      setDialogOpen(false);
    }
  }

  function handleCustomerSelect(item: PosCustomerSearchItem) {
    // Chụp TRƯỚC `setFieldsValue` bên dưới — đây là cả lý do nút Huỷ hoạt động.
    captureCancelSnapshot();
    const u = item.user;
    const a = item.address;
    // TCR-1818 (fix vòng 3) — `customer_user_id` set ngay dưới đây làm `isOverLimitCustomer`
    // đổi giá trị, effect theo dõi biến đó (xem đầu component) tự bật pop-up "Cảnh báo vi
    // phạm" nếu khách vừa chọn đang vượt hạn mức. Không chặn tạo đơn — khác hẳn nút "Duyệt
    // đơn" ở TCR-1823 (có chặn thật theo permission).
    // Birthday tới từ BE dạng ISO (YYYY-MM-DD) nhưng field dùng mask DD/MM/YYYY.
    // Không đổi định dạng => validation báo "Ngày sinh không hợp lệ".
    const rawBirthday = u.birthday ? u.birthday.substring(0, 10) : '';
    const birthdayDisplay = /^\d{4}-\d{2}-\d{2}$/.test(rawBirthday)
      ? `${rawBirthday.slice(8, 10)}/${rawBirthday.slice(5, 7)}/${rawBirthday.slice(0, 4)}`
      : undefined;
    const mirroredDelivery = {
      delivery_name: a.name || u.name || '',
      delivery_phone: a.phone || u.phone || '',
      delivery_citizen_id: a.citizen_id || u.cccd || '',
      delivery_email: a.email || u.email || '',
      delivery_city_id: a.city_id || u.city_id || undefined,
      _deliveryCityName: a.city?.name ?? '',
      delivery_ward_id: a.ward_id || u.ward_id || undefined,
      _deliveryWardName: a.ward?.name ?? '',
      delivery_address: a.address || u.address || '',
    };
    form.setFieldsValue({
      customer_user_id: u._id || undefined,
      // TCR-2129: kênh B2B của khách — field ẩn, dùng để quyết định theo dõi CK chính sách.
      _b2bChannel: u.b2b_channel ?? undefined,
      invoice_same_as_delivery: false,
      user_type: u.user_type ?? 'personal',
      customer_phone: a.phone || u.phone || '',
      customer_name: a.name || u.name || '',
      customer_email: a.email || u.email || '',
      citizen_id: a.citizen_id || '',
      customer_address: a.address || u.address || '',
      customer_city_id: a.city_id || u.city_id || undefined,
      _cityName: a.city?.name ?? '',
      customer_ward_id: a.ward_id || u.ward_id || undefined,
      _wardName: a.ward?.name ?? '',
      // TCR-1281: nhóm khách hàng (vd "Đại lý cấp 2") — `u.group` đã được BE
      // trả đúng (xem TCR-1188) và type khai báo đúng
      // (`PosCustomerSearchUser.group`), nhưng trước bản sửa này KHÔNG có
      // dòng nào đọc field này vào form — dữ liệu rơi mất ngay tại điểm chọn
      // khách, chưa từng tới `SelectedCustomerCard`. `customer_group_id` đi
      // kèm payload tạo đơn nếu BE cần đối chiếu; `_groupName` (cùng quy ước
      // với `_cityName`/`_wardName` — field hiển thị-only, không có
      // `Form.Item`) là nguồn cho badge ở `SelectedCustomerCard`.
      customer_group_id: u.group?._id || undefined,
      _groupName: u.group?.name ?? '',
      customer_birthday: birthdayDisplay,
      invoice_name: u.invoice_organization_name || '',
      invoice_tax_id: u.tax_code || '',
      // Bug thật user báo trực tiếp 2026-08-24: dùng nhầm u.address (Địa chỉ chi tiết, có thể đã
      // bị cắt còn mỗi phần street) thay vì u.invoice_address (đầy đủ, có phường/xã tỉnh/thành) —
      // BE trước đây không trả field này nên phải fallback sai, đã bổ sung ở pos-customer.service.ts.
      invoice_address: u.invoice_address || u.address || '',
      invoice_email: u.tax_email || '',
      buyer_name: u.invoice_buyer_name || '',
      buyer_cccd: u.invoice_buyer_cccd || '',
      amis_account_object_id: u.amis_account_object_id || undefined,
      // TCR-1953 (QA reopen): mã KH AMIS để hiện trên thẻ khách + modal. Ghi `''` (không phải
      // `undefined`) khi khách chưa có mã — thẻ phân biệt "khách tra từ hệ thống nhưng chưa lên
      // AMIS" (hiện "—") với "khách vừa thêm tay" (không có dòng này).
      _amisCode: u.amis_account_object_code ?? '',
      // GIỮ phương thức nhận hàng thu ngân đã bấm ở thanh dưới, chỉ điền
      // `'delivery'` khi form chưa có gì (cùng cách dòng ~144 làm lúc mount).
      // Ghi `'delivery'` vô điều kiện ở đây là bug: thứ tự thao tác bình thường là
      // thêm hàng → bấm "Lấy tại chi nhánh"/"Lưu kho" → mới tra khách, nên MỌI đơn
      // không-giao-hàng sẽ âm thầm nhảy về "Giao hàng" ngay khi chọn khách.
      receive_method: form.getFieldValue('receive_method') ?? 'delivery',
      delivery_same_as_customer: true,
      ...mirroredDelivery,
      delivery_note: '',
      _deliveryNoteTouched: false,
      delivery_add_receiver_to_book: false,
      // TCR-2128 — đổi khách thì bỏ nguồn Sổ địa chỉ của khách trước (address_id của khách A
      // không thuộc sổ khách B). Đoạn đọc sổ bên dưới sẽ ghi lại nguồn của khách mới.
      _deliveryAddressSource: {
        snapshot: takeDeliverySnapshot(mirroredDelivery),
      } satisfies DeliveryAddressSource,
      delivery_save_to_book: false,
      customer_tax_code: u.tax_code || '',
      // Đổi khách (kể cả từ "chưa có khách" sang khách mới) phải xoá điểm đã
      // đổi của khách trước — cùng lớp bug với `clearCustomer()`: điểm redeem
      // gắn với `customer_user_id` cũ, không xoá thì rò sang khách vừa chọn.
      loyalty_redeem_points: undefined,
      loyalty_redeem_value: undefined,
      ...(item.account_manager?.staff_id
        ? { care_staff_id: item.account_manager.staff_id }
        : {}),
    });

    /**
     * TCR-1146 vòng 3: Tab "Thông tin giao nhận" giờ map sang sổ địa chỉ
     * riêng (`customer_addresses`) thay vì mirror hồ sơ khách — lấy đúng địa
     * chỉ MẶC ĐỊNH đầu tiên tìm thấy (BE đã `sort({is_default: -1, ...})`
     * nên phần tử đầu luôn là mặc định nếu có). Set values ở trên vẫn đứng
     * làm fallback khi khách chưa có địa chỉ nào trong sổ, hoặc khi API lỗi.
     */
    const selectedCustomerId = u._id;
    if (selectedCustomerId) {
      customerService
        .getAddresses(selectedCustomerId)
        .then((res) => {
          const list = (res.data ?? []) as unknown as Record<string, unknown>[];
          // TCR-1432 (QA FAILED 2026-08-26): trước đây luôn lấy phần tử ĐẦU
          // (dựa vào BE sort is_default:-1) — nhưng khi khách được AMIS sync
          // SAU KHI đã có sẵn 1 địa chỉ `is_default:true` tự nhập tay/cũ,
          // be-worker CỐ Ý không tự đổi is_default của địa chỉ mới sync (xem
          // amis-account-object-sync.service.ts `buildShippingAddressOps` —
          // `is_default` chỉ set true nếu đây là CustomerAddress ĐẦU TIÊN của
          // khách, để tránh lặp lại bug double-default TCR-1448 đã bị unique
          // index chặn). Hệ quả: `is_default` lúc đó vẫn là địa chỉ CŨ, không
          // phải "Địa chỉ giao hàng" thật trên AMIS — verify DB thật (tenant
          // demo, customer Fanlabs Test/0936000996): is_default trỏ "Số 82,
          // duy tân, dịch vọng hậu" (địa chỉ hồ sơ cũ), trong khi entry AMIS
          // (amis_account_object_id="c250cc35-...") mới là "135 Thái Hà"
          // (đúng theo AMIS "Địa chỉ giao hàng"). Ưu tiên entry có
          // `amis_account_object_id` (địa chỉ giao nhận AMIS đã sync) hơn
          // is_default; chỉ rơi về `is_default`/`list[0]` khi khách không có
          // entry nào từ AMIS (khách tự nhập tay, không qua sync) — giữ đúng
          // hành vi cũ cho nhóm khách đó, không regress.
          const amisEntry = list.find((item) => Boolean(item.amis_account_object_id));
          const def = amisEntry ?? list[0];
          if (!def) return;
          const cityRef = (def.city_id ?? def.city) as AddressLocationRef;
          const wardRef = (def.ward_id ?? def.ward) as AddressLocationRef;
          // TCR-1994: entry AMIS sync có thể thiếu hẳn SĐT/CCCD/email (AMIS không có
          // SĐT cho "Địa chỉ giao hàng") — trước đây ghi đè `''` lên giá trị đã mirror
          // từ hồ sơ khách ở trên. Field liên hệ trống thì lấy bù: entry mặc định →
          // giá trị đang có trong form (hồ sơ khách).
          const defaultEntry = list.find((item) => item.is_default === true) ?? list[0];
          const pickContact = (key: 'phone' | 'citizen_id' | 'email', formKey: string): string =>
            (def[key] as string) ||
            (defaultEntry?.[key] as string) ||
            (form.getFieldValue(formKey) as string) ||
            '';
          const defEntry = def as unknown as AddressBookEntry;
          // Người chính mang đúng SĐT đã điền vào form (entry thiếu SĐT thì lấy bù từ hồ sơ) —
          // nếu để rỗng, ô "Thêm … vào người nhận khác" hiện ngay và ghi chú tự điền thiếu số.
          const filledPhone = pickContact('phone', 'delivery_phone');
          const recipients = entryRecipients(defEntry).map((r) =>
            r.is_main ? { ...r, phone: filledPhone } : r,
          );
          const filled = {
            delivery_same_as_customer: false,
            delivery_name: (def.name as string) || '',
            delivery_phone: filledPhone,
            delivery_citizen_id: pickContact('citizen_id', 'delivery_citizen_id'),
            delivery_email: pickContact('email', 'delivery_email'),
            delivery_city_id: readAddressLocationId(cityRef) || undefined,
            _deliveryCityName: readAddressLocationName(cityRef),
            delivery_ward_id: readAddressLocationId(wardRef) || undefined,
            _deliveryWardName: readAddressLocationName(wardRef),
            delivery_address: (def.address as string) || '',
          };
          const deliveryNote = buildDeliveryNote(defEntry.note, recipients, filled.delivery_phone);
          form.setFieldsValue({
            ...filled,
            // TCR-2384 — ghi chú giao hàng tự điền từ ghi chú địa chỉ + người nhận khác.
            delivery_note: deliveryNote,
            _deliveryNoteTouched: false,
            delivery_add_receiver_to_book: false,
            // TCR-2128 — ghi nguồn: Xác nhận kèm tick "Lưu vào Sổ địa chỉ" sẽ cập nhật ĐÚNG entry
            // này, và ô đó chỉ hiện khi thu ngân sửa khác bản vừa đổ vào.
            _deliveryAddressSource: {
              address_id: def.address_id as string | undefined,
              snapshot: takeDeliverySnapshot(filled),
              recipients,
              note: defEntry.note,
              address_name: defEntry.address_name,
            } satisfies DeliveryAddressSource,
            delivery_save_to_book: false,
          });
        })
        .catch(() => {
          // Giữ nguyên fallback mirror-từ-hồ-sơ đã set ở trên khi API lỗi.
        });
    }

    // Chọn khách ở ô gợi ý: nếu thiếu thông tin bắt buộc thì mở dialog để bổ
    // sung; khách đã đủ thông tin được xác nhận ngay (chỉ compact).
    if (variant === 'compact') {
      const receiveMethod = (form.getFieldValue('receive_method') ?? 'delivery') as
        | 'delivery'
        | 'pickup'
        | 'vault';
      const selectedUserType = (u.user_type ?? 'personal') as UserType;
      const complete =
        validationRequirements.isResolved &&
        isCustomerDialogComplete(
          form.getFieldsValue(true),
          selectedUserType,
          receiveMethod,
          validationRequirements.personalCccdRequired,
          validationRequirements.personalInvoiceOptional,
        );
      if (!complete) {
        setDialogMode('edit');
        setDialogOpen(true);
      }
    }
  }

  return (
    <div
      style={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#fafafa',
      }}
    >
      {!hideHeader && (
        <div
          style={{
            padding: '8px 12px',
            background: '#f0f0f5',
            borderBottom: '1px solid #e0e0e0',
            fontWeight: 700,
            fontSize: 11,
            color: '#555',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Thông tin đơn hàng
        </div>
      )}

      <div
        className="customer-sidebar-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'scroll',
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {/* Loại đơn hàng — inline label + toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={INLINE_LABEL_STYLE}>Loại đơn hàng</Text>
          <div style={{ flex: 1, minWidth: 0 }}>
            <OrderTypeToggle value={orderType} onChange={onOrderTypeChange} />
          </div>
        </div>

        {/* Chi nhánh bán hàng (TCR-630) — sync 2 chiều với header StoreSelector */}
        <StoreBranchField />

        {/* Phiên Livestream — chỉ hiện khi orderType=LIVESTREAM */}
        {orderType === 'LIVESTREAM' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={INLINE_LABEL_STYLE}>Phiên Live</Text>
            <div style={{ flex: 1, minWidth: 0 }}>
              <LiveSessionPicker
                storeId={selectedStoreId}
                value={selectedSessionId}
                onChange={onSessionChange}
              />
            </div>
          </div>
        )}

        {/* Khách hàng: thẻ đã chọn, hoặc ô tìm + nút thêm + nút voucher */}
        {variant === 'inline' ? (
          <>
            {/* Loại khách hàng */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={INLINE_LABEL_STYLE}>Loại khách hàng</Text>
              <div style={{ flex: 1, minWidth: 0 }}>
                <UserTypeToggle value={userType} onChange={handleUserTypeChange} />
              </div>
            </div>
            {/* Tìm khách hàng + voucher (không có nút +) */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CustomerSearchField label="Tìm khách hàng" onSelect={handleCustomerSelect} />
              </div>
              {canViewPosPromotions && (
                <Tooltip title="Chọn khuyến mãi / Voucher">
                  <Button
                    aria-label="Chọn khuyến mãi / Voucher"
                    icon={<GiftOutlined style={{ fontSize: 18 }} />}
                    onClick={() => setVoucherModalOpen(true)}
                    style={{ height: 36, width: 40, flexShrink: 0 }}
                  />
                </Tooltip>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Hàng tìm khách CHỈ hiện khi chưa chọn khách. Đã có khách rồi thì
                "tìm" và "thêm khách mới" đều không còn nghĩa, còn nút voucher
                chuyển vào thẻ khách. Đổi khách = bấm X trên thẻ (xem
                `SelectedCustomerCard`). */}
            {!hasCustomer && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CustomerSearchField label="Tìm khách hàng" onSelect={handleCustomerSelect} />
                </div>
                {canCreateCustomer && (
                  <Tooltip title="Thêm khách hàng mới">
                    <Button
                      aria-label="Thêm khách hàng mới"
                      icon={<PlusOutlined style={{ fontSize: 16 }} />}
                      onClick={openAdd}
                      style={{ height: 36, width: 40, flexShrink: 0 }}
                    />
                  </Tooltip>
                )}
                {canViewPosPromotions && (
                  <Tooltip title="Chọn khuyến mãi / Voucher">
                    <Button
                      aria-label="Chọn khuyến mãi / Voucher"
                      icon={<GiftOutlined style={{ fontSize: 18 }} />}
                      onClick={() => setVoucherModalOpen(true)}
                      style={{ height: 36, width: 40, flexShrink: 0 }}
                    />
                  </Tooltip>
                )}
              </div>
            )}
            {/* Nhắc chọn khách — chỉ hiện sau khi bấm "Ghi hoá đơn và thanh toán"
                mà chưa có khách, tự tắt ngay khi user chọn/tạo khách. */}
            {showNoCustomerAlert && !hasCustomer && !dialogOpen && (
              <div
                role="alert"
                style={{
                  border: '1px solid #ffa39e',
                  background: '#fff1f0',
                  color: '#cf1322',
                  borderRadius: 6,
                  padding: '8px 12px',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <ExclamationCircleOutlined style={{ fontSize: 14, flexShrink: 0 }} />
                <span>Xin vui lòng chọn hoặc tạo mới khách hàng</span>
              </div>
            )}

            {/* Thẻ thông tin khách. Vẫn hiện khi dialog đang mở: thu ngân cần đối
                chiếu panel "Thông tin đơn hàng" trong lúc sửa thông tin khách. */}
            {hasCustomer && (
              <SelectedCustomerCard
                userType={userType}
                name={
                  (form.getFieldValue('customer_name') as string) ||
                  (form.getFieldValue('invoice_name') as string) ||
                  (form.getFieldValue('buyer_name') as string) ||
                  ''
                }
                phone={customerPhone ?? ''}
                address={[
                  form.getFieldValue('delivery_address'),
                  form.getFieldValue('_deliveryWardName'),
                  form.getFieldValue('_deliveryCityName'),
                ]
                  .filter(Boolean)
                  .join(', ')}
                onEdit={openEdit}
                onClear={clearCustomer}
                // TCR-1696 — `SelectedCustomerCard` đã có hợp đồng "không truyền `onVoucher`
                // ⇒ không render icon quà", nên chỉ cần không truyền thay vì thêm prop quyền.
                onVoucher={canViewPosPromotions ? () => setVoucherModalOpen(true) : undefined}
                // TCR-1570: card hiển thị ĐỊA CHỈ NHẬN HÀNG (delivery_*), không phải
                // địa chỉ hồ sơ/hoá đơn của khách — trước đó card đọc nhầm
                // `customer_address`/`_wardName`/`_cityName` (billing).
                // TCR-1281: cùng cách đọc `_cityName`/`_wardName` ngay phía
                // trên (field hiển thị-only, không có `Form.Item`, ghi bởi
                // `handleCustomerSelect`) — `_groupName` là nguồn cho badge
                // nhóm khách hàng trên thẻ đã chọn.
                groupName={(form.getFieldValue('_groupName') as string) || undefined}
                amisCode={form.getFieldValue('_amisCode') as string | undefined}
                loyalty={loyaltyBadge}
                extraAction={customerCardExtra}
                isOverLimit={isOverLimitCustomer}
                onOverLimitClick={reopenOverLimitWarning}
              />
            )}

            {/* [BỎ N.BÁN + NV CHĂM SÓC — LỊCH SỬ] Dropdown "Nhân viên chăm sóc" từng ở
                ĐÂY đã bị gỡ (2026-07-30, lý do: chiếm chỗ, UI lúc đó không dùng). Đã
                HIỆN LẠI ở vị trí MỚI (trên ô Ghi chú đơn hàng) tại `Orders/edit.tsx` và
                `POS/index.tsx` — xem `CareStaffAsyncSelect`. KHÔNG khôi phục lại đoạn
                code cũ dưới comment này, nó tạo UI sai vị trí. */}

            {/* Hạn giao hàng — chỉ đơn đặt trước, bắt buộc. BE nhận số ngày và tự
                derive delivery_date, nên ở đây là input số ngày chứ không phải DatePicker. */}
            {hasCustomer && fulfillmentMode === 'PREORDER' && (
              <Form.Item
                label="Hạn giao hàng"
                name="delivery_days"
                rules={deliveryDaysRules}
                style={{ margin: 0 }}
              >
                <DeliveryDaysSelect />
              </Form.Item>
            )}
          </>
        )}

        {/* Chip các voucher đang chọn */}
        {vouchers.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {vouchers.map((v) => (
              <div
                key={v.code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  background: '#fff8f9',
                  border: '1px solid #a4193d',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                <GiftOutlined style={{ color: '#a4193d' }} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 600,
                    color: '#a4193d',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={`${v.code} - ${v.label}`}
                  >
                    {v.code} - {v.label}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>
                    -{formatVND(v.discountAmount)}
                  </span>
                </span>
                <CloseCircleOutlined
                  onClick={() => {
                    const next = vouchers.filter((x) => x.code !== v.code);
                    form.setFieldsValue({
                      vouchers: next,
                      voucher_discount: sumVoucherDiscount(next),
                    });
                    // TCR-1074 final review (Fix #1): gỡ voucher qua nút "x" là
                    // đường CHÍNH thu ngân dùng để bỏ voucher — phải đối xứng với
                    // chiều ngược lại (`removeItem` ở `POS/index.tsx` gỡ voucher
                    // khi thu ngân xoá dòng gift). Không báo lên
                    // `onGiftItemsChange` thì dòng quà 0đ mồ côi ở lại giỏ hàng.
                    // `onGiftItemsChange` nhận TOÀN BỘ danh sách gift item còn lại
                    // (không phải toàn giỏ) — khớp hợp đồng của `VoucherModal.onConfirm`.
                    //
                    // `isVoucherGiftItem`, KHÔNG `i.is_gift` thô (fix round 1,
                    // C1): hợp đồng của `onGiftItemsChange` là "danh sách quà
                    // VOUCHER còn lại", không phải "mọi dòng is_gift". Dùng
                    // `is_gift` thô ở đây từng lẫn cả quà CTKM vào payload —
                    // caller (`POS/index.tsx`/`Orders/edit.tsx`) giữ nguyên quà
                    // CTKM trong `prev` (đúng, dùng `isVoucherGiftItem`) RỒI nối
                    // thêm CHÍNH nó từ payload lẫn này ⇒ hai dòng cùng `id` ⇒
                    // `computePosMoney`/`groupAndAllocateVat` ném lỗi key trùng
                    // "goods:<id>" ⇒ màn hình trắng khi tính tiền.
                    onGiftItemsChange?.(
                      cartItems.filter(
                        (i) =>
                          isVoucherGiftItem(i) &&
                          i.gift_promotion_id &&
                          i.gift_promotion_id !== v.code,
                      ),
                    );
                  }}
                  style={{ color: '#a4193d', cursor: 'pointer', fontSize: 16 }}
                  role="button"
                  aria-label={`Bỏ voucher ${v.code}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Chip khuyến mãi tự động áp dụng — TCR-1069. Không cho bỏ (không có
            CloseCircleOutlined): đơn còn đủ điều kiện thì KM còn áp, không phải
            lựa chọn thủ công như voucher. Màu xanh để phân biệt trực quan với
            voucher (đỏ thương hiệu) — tự động vs. do thu ngân chọn.
            review round 3 (finding A): `showAutoPromotions` gate — sidebar này
            cũng bị `Orders/edit.tsx` dựng lại với giỏ hàng khác rỗng, nơi
            `OrderInfoPanel` không nhận `promotionDiscount` nên chip ở đó sẽ
            nói một số mà tổng tiền không hề đổi theo. */}
        {showAutoPromotions && appliedPromotions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {appliedPromotions.map((p) => (
              <div
                key={p.promotion_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  background: '#f6ffed',
                  border: '1px solid #52c41a',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                <GiftOutlined style={{ color: '#52c41a' }} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 600,
                    color: '#389e0d',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={p.name}
                  >
                    {p.name}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>
                    {/* review round 3 (finding B): CTKM tặng-hàng thuần (buy-X-get-Y)
                        hợp lệ có `discount_amount: 0` (BE vẫn coi là "đã áp dụng" —
                        xem `promotion-engine.service.ts`, điều kiện `discount_amount > 0
                        || free_items.length > 0`). "-0 ₫" nói dối là không giảm gì; ẩn
                        hẳn phần tiền khi bằng 0 thay vì hiện số sai. Chi tiết quà tặng
                        (free_items) CHƯA hiện ở bản này — ngoài phạm vi round này, không
                        phải thiếu sót bỏ quên. */}
                    Tự động áp dụng{p.discount_amount > 0 ? ` · -${formatVND(p.discount_amount)}` : ''}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Chiết khấu tích luỹ (CK thương mại trừ thẳng) — chỉ KH tổ chức, cần quyền B2B riêng */}
        {userType === 'organization' && hasCustomer && canApplyB2bTradeDiscount && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Button
              block
              icon={<PercentageOutlined />}
              onClick={() => setTradeDiscountModalOpen(true)}
              style={
                tradeDiscount > 0
                  ? { borderColor: '#a4193d', color: '#a4193d', fontWeight: 600 }
                  : undefined
              }
            >
              Chiết khấu tích luỹ B2B
            </Button>
            {tradeDiscount > 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  background: '#fff8f9',
                  border: '1px solid #a4193d',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                <PercentageOutlined style={{ color: '#a4193d' }} />
                <span style={{ flex: 1, fontWeight: 600, color: '#a4193d' }}>
                  {tradeDiscountNote || 'Chiết khấu thương mại'}
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 400, opacity: 0.85 }}>
                    -{formatVND(tradeDiscount)}
                  </span>
                </span>
                <CloseCircleOutlined
                  onClick={() => form.setFieldsValue({ trade_discount: 0, trade_discount_note: undefined })}
                  style={{ color: '#a4193d', cursor: 'pointer', fontSize: 16 }}
                  role="button"
                  aria-label="Bỏ chiết khấu tích luỹ B2B"
                />
              </div>
            ) : null}
          </div>
        )}

        {/* Loyalty badge đứng riêng cho variant inline (compact đã nhúng vào card). */}
        {variant === 'inline' && loyaltyBadge}

        {/* Hidden Form.Items so Form has the keys */}
        <Form.Item name="_b2bChannel" hidden>
          <input />
        </Form.Item>
        <Form.Item name="customer_user_id" hidden>
          <input />
        </Form.Item>
        {/* customer_phone phải được đăng ký ở đây (compact mode) để Form.useWatch
            phản ứng khi chọn khách — nếu không, thẻ SelectedCustomerCard không hiện
            vì field chỉ tồn tại bên trong CustomerDialog (Modal chưa mở). */}
        <Form.Item name="customer_phone" hidden>
          <input />
        </Form.Item>
        <Form.Item name="user_type" hidden>
          <input />
        </Form.Item>
        <Form.Item name="_cityName" hidden>
          <input />
        </Form.Item>
        <Form.Item name="_wardName" hidden>
          <input />
        </Form.Item>
        <Form.Item name="vouchers" hidden>
          <input />
        </Form.Item>
        <Form.Item name="voucher_discount" hidden>
          <input />
        </Form.Item>
        <Form.Item name="promotion_discount" hidden>
          <input />
        </Form.Item>
        {/* Khoản giảm phí vận chuyển (freeship) — field ẩn để payload tạo đơn
            và `computePosMoney` cùng đọc một nguồn, y như `promotion_discount`. */}
        <Form.Item name="shipping_discount" hidden>
          <input />
        </Form.Item>
        {/* TCR-1298 — "có freeship đang áp dụng" (KM tự động HOẶC voucher nhập
            tay loại free_shipping), độc lập với `shipping_discount` vì voucher
            nhập tay chưa có số tiền giảm thật (BE luôn trả 0, xem
            `hasFreeshipVoucher`). POS/index.tsx và Orders/edit.tsx đọc field
            này để tự khoá dropdown "Người trả phí vận chuyển" về Người bán —
            xem `OrderInfoPanel.freeshipActive`. */}
        <Form.Item name="has_freeship_voucher" hidden>
          <input />
        </Form.Item>
        <Form.Item name="promotion_snapshot_id" hidden>
          <input />
        </Form.Item>
        {/* review round 3, finding C: cờ "đang đánh giá KM tự động" — POSPage
            đọc field này để chặn checkout trong lúc debounce/round-trip mạng
            còn chạy, cùng cách `promotion_discount` ngay trên. */}
        <Form.Item name="promotion_evaluating" hidden>
          <input />
        </Form.Item>
        <Form.Item name="trade_discount" hidden>
          <input />
        </Form.Item>
        <Form.Item name="trade_discount_note" hidden>
          <input />
        </Form.Item>
        {/* TCR-977: Phụ thu nhập ở OrderInfoPanel qua Form.useWatch/setFieldsValue
            như voucher_discount/trade_discount ở trên — KHÔNG đăng ký Form.Item thì
            antd Form không coi đây là field thật, setFieldsValue ghi vào store
            nhưng useWatch không đồng bộ lại ổn định (gõ xong mất giá trị lúc blur). */}
        <Form.Item name="surcharge_amount" hidden>
          <input />
        </Form.Item>
        <Form.Item name="loyalty_redeem_points" hidden>
          <input />
        </Form.Item>
        <Form.Item name="loyalty_redeem_value" hidden>
          <input />
        </Form.Item>

        {variant === 'inline' && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Collapse
              activeKey={activeKeys}
              onChange={(keys) => setActiveKeys(keys as string[])}
              ghost
              size="small"
              items={[
                {
                  key: 'delivery',
                  label: 'Thông tin giao nhận',
                  forceRender: true,
                  children: (
                    <>
                      <CustomerCoreFields
                        form={form}
                        storeId={selectedStoreId}
                        cccdMasked={cccdMasked}
                        onRevealCccd={onRevealCccd}
                      />
                      <DeliveryExtraFields
                        form={form}
                        fulfillmentMode={fulfillmentMode}
                        cityId={cityId}
                        cccdMasked={cccdMasked}
                        onRevealCccd={onRevealCccd}
                      />
                    </>
                  ),
                },
                {
                  key: 'invoice',
                  label: 'Thông tin xuất hoá đơn',
                  forceRender: true,
                  children: (
                    <InvoiceSection
                      form={form}
                      userType={userType}
                      cccdMasked={cccdMasked}
                      onRevealCccd={onRevealCccd}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </div>

      <VoucherModal
        open={voucherModalOpen}
        onClose={() => setVoucherModalOpen(false)}
        cartItems={cartItems}
        customerId={customerUserId}
        storeId={selectedStoreId}
        fulfillmentMode={fulfillmentMode}
        paymentMethod={paymentMethod}
        // TCR-1069: cùng một kết quả evaluate đang nuôi khu vực chip khuyến
        // mãi bên trên — modal khỏi gọi lại API và không bao giờ nói khác chip.
        evaluation={evaluateQuery.data}
        // TCR-1074 final review (Fix #2): chỉ bật resolve gift item khi
        // `onGiftItemsChange` được truyền — `Orders/edit.tsx` không truyền nên
        // ở đó `false`, VoucherModal bỏ qua toàn bộ vòng resolve quà tặng.
        resolveGiftItems={!!onGiftItemsChange}
        onConfirm={(vs, giftItems) => {
          form.setFieldsValue({
            vouchers: vs,
            voucher_discount: sumVoucherDiscount(vs),
          });
          onGiftItemsChange?.(giftItems);
          setVoucherModalOpen(false);
        }}
        selectedCodes={vouchers.map((v) => v.code)}
      />

      <TradeDiscountModal
        open={tradeDiscountModalOpen}
        onClose={() => setTradeDiscountModalOpen(false)}
        orderTotal={computePosTotals(cartItems).grandTotal}
        tradeCap={tradeCap}
        previewTradePayable={previewTradePayable}
        initialAmount={tradeDiscount}
        customerId={customerUserId}
        orderId={rebateOrderId}
        onConfirm={(result) => {
          form.setFieldsValue({
            trade_discount: result.amount,
            trade_discount_note: result.amount > 0 ? (result.note || 'Chiết khấu tích luỹ') : undefined,
          });
          setTradeDiscountModalOpen(false);
        }}
      />

      {variant === 'compact' && (
        <CustomerDialog
          open={dialogOpen}
          mode={dialogMode}
          form={form}
          userType={userType}
          validationPolicy={validationPolicy}
          onRetryValidationPolicy={onRetryValidationPolicy}
          onUserTypeChange={handleUserTypeChange}
          onClose={cancelDialog}
          confirmLoading={savingCustomer}
          onSaved={handleDialogSaved}
          cccdMasked={cccdMasked}
          onRevealCccd={onRevealCccd}
          abilities={customerAbilities}
        />
      )}

      {/* TCR-1818 — pop-up cảnh báo ĐỘC LẬP (yêu cầu PO sau khi bản banner/inline bị đánh
          FAILED vì "lẫn vào các dòng, khó để ý"). Chỉ 1 nút "Tôi đã hiểu" — không chặn lên
          đơn, khác nút "Duyệt đơn" ở TCR-1823 (Modal.confirm có chặn thật theo quyền). */}
      <Modal
        open={overLimitWarningCustomerId !== null}
        title={
          <span style={{ color: '#faad14' }}>
            <ExclamationCircleOutlined style={{ marginRight: 8 }} />
            Cảnh báo vi phạm
          </span>
        }
        onOk={() => setOverLimitWarningCustomerId(null)}
        onCancel={() => setOverLimitWarningCustomerId(null)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setOverLimitWarningCustomerId(null)}>
            Tôi đã hiểu
          </Button>,
        ]}
      >
        Khách hàng đang có công nợ vượt hạn mức. Hạn chế lên đơn hàng
      </Modal>
    </div>
  );
});
